/**
 * Telegraphs no chão — o indicador de perigo que aparece ANTES do dano (elite ground
 * smash, boss howl/leap/charge). Puramente visual e reciclado via pool; o dano/timing
 * é responsabilidade de quem chama. "circle" = AoE radial; "lane" = corredor (charge).
 */
import * as THREE from "three";
import { Pool } from "../utils/Pool";

interface Tele {
  group: THREE.Group;
  fill: THREE.Mesh;
  base: THREE.Mesh;
  extra: THREE.Mesh | null; // shard: cristal que emerge · wave: anel que expande
  kind: "circle" | "lane" | "shard" | "wave";
  life: number;
  dur: number;
  radius: number; // wave/shard: raio máximo
  id: number; // handle p/ cancelamento (0 = sem handle ativo)
}

const DANGER = 0xff3344;

export class TelegraphSystem {
  private readonly active: Tele[] = [];
  private readonly circlePool: Pool<Tele>;
  private readonly lanePool: Pool<Tele>;
  private readonly shardPool: Pool<Tele>;
  private readonly wavePool: Pool<Tele>;
  private readonly ringGeo = new THREE.RingGeometry(0.9, 1, 40);
  private readonly discGeo = new THREE.CircleGeometry(1, 40);
  private readonly planeGeo = new THREE.PlaneGeometry(1, 1);
  private readonly thinRingGeo = new THREE.RingGeometry(0.82, 1, 48);
  private readonly spikeGeo = new THREE.ConeGeometry(0.5, 1.6, 5);
  private nextId = 1;

  constructor(private readonly scene: THREE.Scene) {
    this.circlePool = new Pool<Tele>(() => this.makeCircle(), (t) => this.hide(t), 4);
    this.lanePool = new Pool<Tele>(() => this.makeLane(), (t) => this.hide(t), 2);
    this.shardPool = new Pool<Tele>(() => this.makeShard(), (t) => this.hide(t), 6);
    this.wavePool = new Pool<Tele>(() => this.makeWave(), (t) => this.hide(t), 2);
  }

  private poolFor(kind: Tele["kind"]): Pool<Tele> {
    return kind === "circle" ? this.circlePool : kind === "lane" ? this.lanePool : kind === "shard" ? this.shardPool : this.wavePool;
  }

  /** SHARD: rachadura que cresce + cristal que emerge (Shard Rain). WHERE/WHEN/SIZE. */
  shard(x: number, z: number, radius: number, duration: number, color = DANGER): number {
    const t = this.shardPool.acquire();
    (t.base.material as THREE.MeshBasicMaterial).color.setHex(color);
    (t.fill.material as THREE.MeshBasicMaterial).color.setHex(color);
    if (t.extra) (t.extra.material as THREE.MeshBasicMaterial).color.setHex(color);
    t.group.position.set(x, 0.06, z);
    t.group.scale.setScalar(radius);
    t.group.visible = true;
    t.life = 0;
    t.dur = duration;
    t.radius = radius;
    t.id = this.nextId++;
    this.active.push(t);
    return t.id;
  }

  /** WAVE: anel que expande do centro até maxRadius (Core Pulse). O dano acompanha o anel. */
  wave(x: number, z: number, maxRadius: number, duration: number, color = DANGER): number {
    const t = this.wavePool.acquire();
    (t.base.material as THREE.MeshBasicMaterial).color.setHex(color);
    t.group.position.set(x, 0.06, z);
    t.group.scale.setScalar(0.1);
    t.group.visible = true;
    t.life = 0;
    t.dur = duration;
    t.radius = maxRadius;
    t.id = this.nextId++;
    this.active.push(t);
    return t.id;
  }

  /** AoE radial em (x,z) com dado raio; some quando `duration` termina (= impacto). Retorna handle. */
  circle(x: number, z: number, radius: number, duration: number, color = DANGER): number {
    const t = this.circlePool.acquire();
    (t.base.material as THREE.MeshBasicMaterial).color.setHex(color);
    (t.fill.material as THREE.MeshBasicMaterial).color.setHex(color);
    t.group.position.set(x, 0.06, z);
    t.group.scale.setScalar(radius);
    t.group.visible = true;
    t.life = 0;
    t.dur = duration;
    t.id = this.nextId++;
    this.active.push(t);
    return t.id;
  }

  /** Corredor (charge): de (x,z) na direção `angle`, com comprimento/largura. Retorna handle. */
  lane(x: number, z: number, angle: number, length: number, width: number, duration: number, color = DANGER): number {
    const t = this.lanePool.acquire();
    (t.base.material as THREE.MeshBasicMaterial).color.setHex(color);
    (t.fill.material as THREE.MeshBasicMaterial).color.setHex(color);
    t.group.position.set(x, 0.06, z);
    t.group.rotation.y = angle;
    // base cobre todo o corredor; o mesh interno cresce ao longo do comprimento
    t.base.scale.set(width, 1, length);
    t.base.position.z = length / 2;
    t.fill.scale.set(width, 1, length);
    t.fill.position.z = length / 2;
    t.group.visible = true;
    t.life = 0;
    t.dur = duration;
    t.id = this.nextId++;
    this.active.push(t);
    return t.id;
  }

  /** Cancela um telegraph pelo handle (ataque abortado/interrompido não deixa "fantasma"). */
  cancel(id: number): void {
    if (id <= 0) return;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const t = this.active[i];
      if (t.id !== id) continue;
      (t.kind === "circle" ? this.circlePool : this.lanePool).release(t);
      this.active.splice(i, 1);
      return;
    }
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const t = this.active[i];
      t.life += dt;
      const p = Math.min(1, t.life / t.dur);
      if (t.kind === "circle") {
        t.fill.scale.setScalar(p);
        (t.fill.material as THREE.MeshBasicMaterial).opacity = 0.25 + p * 0.35;
      } else if (t.kind === "lane") {
        t.fill.scale.z = p;
        t.fill.position.z = (t.base.scale.z * p) / 2;
        (t.fill.material as THREE.MeshBasicMaterial).opacity = 0.25 + p * 0.35;
      } else if (t.kind === "shard") {
        // 0-40% rachadura · 40-80% energia · 80-100% cristal emerge (ameaça máxima)
        t.fill.scale.setScalar(Math.min(1, p * 2.2)); // energia sobe cedo
        (t.fill.material as THREE.MeshBasicMaterial).opacity = 0.2 + p * 0.5;
        (t.base.material as THREE.MeshBasicMaterial).opacity = 0.4 + Math.sin(t.life * 20) * 0.15 * p;
        if (t.extra) {
          const emerge = Math.max(0, (p - 0.55) / 0.45); // cristal sobe no fim
          t.extra.visible = emerge > 0;
          t.extra.scale.set(0.5 + emerge * 0.5, emerge, 0.5 + emerge * 0.5);
          t.extra.position.y = emerge * 0.8;
        }
      } else if (t.kind === "wave") {
        // anel expande do centro até radius (dano acompanha o anel — sincronizado)
        const rad = 0.1 + p * t.radius;
        t.group.scale.setScalar(rad);
        (t.base.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - p * 0.5);
      }
      if (t.life >= t.dur) {
        this.poolFor(t.kind).release(t);
        this.active.splice(i, 1);
      }
    }
  }

  clear(): void {
    for (const t of this.active) this.poolFor(t.kind).release(t);
    this.active.length = 0;
  }

  private makeCircle(): Tele {
    const group = new THREE.Group();
    const base = new THREE.Mesh(this.ringGeo, this.dangerMat(0.5));
    const fill = new THREE.Mesh(this.discGeo, this.dangerMat(0.3));
    base.rotation.x = -Math.PI / 2;
    fill.rotation.x = -Math.PI / 2;
    fill.position.y = 0.005;
    group.add(base, fill);
    group.visible = false;
    this.scene.add(group);
    return { group, fill, base, extra: null, kind: "circle", life: 0, dur: 1, radius: 0, id: 0 };
  }

  private makeLane(): Tele {
    const group = new THREE.Group();
    const base = new THREE.Mesh(this.planeGeo, this.dangerMat(0.4));
    const fill = new THREE.Mesh(this.planeGeo, this.dangerMat(0.3));
    base.rotation.x = -Math.PI / 2;
    fill.rotation.x = -Math.PI / 2;
    fill.position.y = 0.005;
    group.add(base, fill);
    group.visible = false;
    this.scene.add(group);
    return { group, fill, base, extra: null, kind: "lane", life: 0, dur: 1, radius: 0, id: 0 };
  }

  private makeShard(): Tele {
    const group = new THREE.Group();
    const base = new THREE.Mesh(this.ringGeo, this.dangerMat(0.5)); // rachadura/anel
    const fill = new THREE.Mesh(this.discGeo, this.dangerMat(0.25)); // energia
    base.rotation.x = -Math.PI / 2;
    fill.rotation.x = -Math.PI / 2;
    fill.position.y = 0.005;
    const spike = new THREE.Mesh(this.spikeGeo, this.dangerMat(0.85)); // cristal que emerge
    spike.position.y = 0;
    spike.visible = false;
    group.add(base, fill, spike);
    group.visible = false;
    this.scene.add(group);
    return { group, fill, base, extra: spike, kind: "shard", life: 0, dur: 1, radius: 0, id: 0 };
  }

  private makeWave(): Tele {
    const group = new THREE.Group();
    const base = new THREE.Mesh(this.thinRingGeo, this.dangerMat(0.6)); // anel fino que expande
    base.rotation.x = -Math.PI / 2;
    const fill = base; // wave usa só o anel
    group.add(base);
    group.visible = false;
    this.scene.add(group);
    return { group, fill, base, extra: null, kind: "wave", life: 0, dur: 1, radius: 0, id: 0 };
  }

  private dangerMat(opacity: number): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
      color: DANGER,
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }

  private hide(t: Tele): void {
    t.group.visible = false;
  }
}
